import React from 'react';

import { configs } from '$configs';
import { SORT, COLORS } from '$gbusiness/enums';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { defaultDataSource, defaultStyles, TableModel } from '$gbusiness/models/table';
import CellModel from '$gbusiness/models/cell';
import { deriveRawToCoupon } from '$fbusiness/models/coupon';
import { Button, Chip } from '@mui/material';
import { COUPON_TYPE } from '$fbusiness/enums/couponType';

export const COUPON_TABLE_CONFIG: TableModel = {
  dataSource: {
    ...defaultDataSource,
    endpoint: configs.api.coupon,
    method: 'GET',
    deriveToModel: deriveRawToCoupon,
    defaultSortKey: 'created_at',
    defaultSortOrder: SORT.DSC,
    shouldVirtualize: false,
    pageSize: 20,
  },
  styles: {
    ...defaultStyles,
    color: COLORS.LIGHT,
    fontSize: '1em',
    minWidth: 300,
    maxWidth: 1200,
    cellPadding: ` 8px 4px`,
    isStripped: true,
  },
  filter: {
    color: COLORS.PRIMARY,
  },
};

const formatDiscount = (row) => {
  switch (row.type) {
    case COUPON_TYPE.PERCENT:
      return `${row.discount_value}% off`;
    case COUPON_TYPE.FIXED:
      return `$${row.discount_value} off`;
    case COUPON_TYPE.FREE_SHIPPING:
      return 'Free Shipping';
    default:
      return '-';
  }
};

const formatAppliesTo = (row) => {
  switch (row.applies_to) {
    case 'all':
      return 'All Products';
    case 'categories':
      return 'Specific Categories';
    case 'products':
      return 'Specific Products';
    default:
      return '-';
  }
};

export const COUPON_TABLE = (): Array<CellModel> => [
  {
    label: 'SCREEN.COUPONS.COLS.CODE',
    value: 'code',
    alwaysShow: true,
    sortable: SORT.ASC,
    width: 150,
    component: (row) => <div style={{ fontWeight: 600, fontFamily: 'monospace' }}>{row.code}</div>,
  },
  {
    label: 'SCREEN.COUPONS.COLS.NAME',
    value: 'name',
    sortable: SORT.ASC,
    width: 250,
  },
  {
    label: 'SCREEN.COUPONS.COLS.DISCOUNT',
    value: 'discount_value',
    component: (row) => <div>{formatDiscount(row)}</div>,
    sortable: SORT.ASC,
    width: 150,
  },
  {
    label: 'SCREEN.COUPONS.COLS.APPLIES_TO',
    value: 'applies_to',
    component: (row) => <div>{formatAppliesTo(row)}</div>,
    width: 180,
  },
  {
    label: 'SCREEN.COUPONS.COLS.USAGE',
    value: 'current_redemptions',
    component: (row) => (
      <div>
        {row.current_redemptions}
        {row.max_redemptions ? ` / ${row.max_redemptions}` : ' / ∞'}
      </div>
    ),
    width: 120,
  },
  {
    label: 'SCREEN.COUPONS.COLS.STATUS',
    value: 'is_active',
    component: (row) => {
      if (!row.is_active) {
        return <Chip label="Inactive" size="small" color="default" />;
      }
      if (row.is_expired) {
        return <Chip label="Expired" size="small" color="error" />;
      }
      if (row.is_not_started) {
        return <Chip label="Scheduled" size="small" color="info" />;
      }
      return <Chip label="Active" size="small" color="success" />;
    },
    width: 120,
  },
  {
    label: 'SCREEN.ITEMS.COLS.ACTION',
    value: '',
    className: 'action nowrap',
    align: 'center',
    component: (row, actions) => {
      return (
        <Button
          className="left"
          color="secondary"
          fullWidth
          onClick={(e) => actions.handlePopoverOpen(e, row)}>
          <MoreHorizIcon />
        </Button>
      );
    },
    width: 100,
  },
];
